import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { motion } from "framer-motion";
import {
  LayoutTemplate, BarChart3, Users, ShieldCheck, Sun, Moon, LogOut, Menu, BookOpen,
} from "lucide-react";
import { getUser, clearToken, listDocs } from "../lib/api";
import { useTheme } from "../hooks/useTheme";
import "../studio.css";

export default function AppShell({ children }) {
  const nav = useNavigate();
  const loc = useLocation();
  const user = getUser();
  const { theme, toggle } = useTheme();
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem("cts-nav-expanded") !== "0"; } catch { return true; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.role === "platform_admin";
  const [docCount, setDocCount] = useState(0);
  useEffect(() => { listDocs().then((d) => setDocCount((d || []).length)).catch(() => {}); }, []);

  const setExp = (v) => {
    setExpanded(v);
    try { localStorage.setItem("cts-nav-expanded", v ? "1" : "0"); } catch (e) { console.debug(e); }
  };

  const params = new URLSearchParams(loc.search);
  const tab = params.get("tab") || "standard";
  const studioActive = loc.pathname === "/";

  const items = isAdmin
    ? [
        {
          to: "/", label: "Studio", icon: LayoutTemplate, testid: "nav-studio", active: studioActive,
          sub: [
            { to: "/?tab=standard", label: "Standard", key: "standard" },
            { to: "/?tab=mine", label: "My Templates", key: "mine" },
          ],
        },
        { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
        { to: "/team", label: "Team", icon: Users, testid: "nav-team" },
        { to: "/admin", label: "Admin Console", icon: ShieldCheck, testid: "nav-admin" },
      ]
    : [
        {
          to: "/", label: "Studio", icon: LayoutTemplate, testid: "nav-studio", active: studioActive,
          sub: [
            { to: "/?tab=standard", label: "Standard", key: "standard" },
            { to: "/?tab=mine", label: "My Templates", key: "mine" },
          ],
        },
        { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
        { to: "/team", label: "Team", icon: Users, testid: "nav-team" },
      ];

  if (docCount > 0) items.push({ to: "/docs", label: "Docs", icon: BookOpen, testid: "nav-docs" });

  const initials = (user?.name || user?.email || "?").trim().slice(0, 2).toUpperCase();
  const signOut = () => { clearToken(); nav("/login"); };
  const go = (to) => { nav(to); setMobileOpen(false); };

  return (
    <div className="cts-scope">
      <Toaster position="bottom-center" />
      <div className="shell">
        {mobileOpen && <div className="nav-scrim" onClick={() => setMobileOpen(false)} />}
        <motion.aside
          className={`sidenav ${expanded ? "exp" : "col"} ${mobileOpen ? "mobile-open" : ""}`}
          animate={{ width: expanded ? 224 : 66 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          data-testid="sidenav"
        >
          <div className="sn-top">
            <button className="sn-menu-btn" onClick={() => setExp(!expanded)} data-testid="nav-toggle"
              title={expanded ? "Collapse menu" : "Expand menu"} aria-label="Toggle menu">
              <Menu size={20} />
            </button>
          </div>
          <div className="sn-brand" onClick={() => go("/")}>
            <div className="glyph" />
            {expanded && <div className="sn-title">Studio<span>CloudTV</span></div>}
          </div>

          <nav className="sn-nav">
            {items.map((it) => {
              const Icon = it.icon;
              const active = it.active !== undefined ? it.active : loc.pathname === it.to;
              return (
                <div key={it.to} className="sn-group">
                  <button
                    className={`sn-item ${active ? "active" : ""}`}
                    onClick={() => go(it.to)}
                    title={expanded ? "" : it.label}
                    data-testid={it.testid}
                  >
                    <Icon size={18} strokeWidth={2} />
                    {expanded && <span>{it.label}</span>}
                    {active && <span className="sn-active-bar" />}
                  </button>
                  {expanded && it.sub && active && (
                    <div className="sn-sub" data-testid="nav-substudio">
                      {it.sub.map((s) => (
                        <button
                          key={s.key}
                          className={`sn-subitem ${tab === s.key ? "active" : ""}`}
                          onClick={() => go(s.to)}
                          data-testid={`nav-sub-${s.key}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="sn-foot">
            <button className="sn-item" onClick={toggle} title={expanded ? "" : "Toggle theme"} data-testid="theme-toggle">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
              {expanded && <span>{theme === "dark" ? "Light mode" : "Dark mode"}</span>}
            </button>

            <div className={`sn-user ${expanded ? "" : "collapsed"}`} title={user?.email} data-testid="sidebar-user">
              <div className="avatar">{initials}</div>
              {expanded && (
                <div className="sn-user-meta">
                  <b>{user?.companyName || (isAdmin ? "Platform" : "—")}</b>
                  <span>{user?.email}</span>
                </div>
              )}
            </div>

            <button className="sn-item" onClick={signOut} title={expanded ? "" : "Sign out"} data-testid="sidebar-signout">
              <LogOut size={18} />
              {expanded && <span>Sign out</span>}
            </button>
          </div>
        </motion.aside>

        <main className="shell-main">
          <button className="nav-hamburger" onClick={() => setMobileOpen(true)} data-testid="nav-hamburger" aria-label="Open menu">
            <Menu size={20} />
          </button>
          {children}
        </main>
      </div>
    </div>
  );
}
